import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import strings from "translate/language";
import { handleListResponse, isEmpty, seo } from "utils/utils";
import {
  getVisiterList,
  addAppointment,
  resetAddAppointment,
} from "../../reduxStore/action/appointmentActions";
import Breadcrumb from "component/common/breadcrumb";
import AppointmentScheduleFields from "./AppointmentScheduleFields";
import "./AppointmentListing.css";
import { getAgentProperty } from "reduxStore/action/propertyActions";
import {
  SourcingManager,
  SourcingTL,
  sourcing_head_id,
} from "../../utils/constants";

const AddAppointment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formVaild, setFormVaild] = useState(null);
  const [form, setForm] = useState({});
  const [apiResponse, setApiResponse] = useState([]);
  const params = new URLSearchParams(window.location.search);
  const leadId = params.get("lead_id");
  const propertyId = params.get("property_id");

  useEffect(() => {
    if (leadId) {
      setForm({ ...form, lead_id: leadId, property_id: propertyId });
    }
  }, [leadId, propertyId]);

  const getVisitorListSelector = useSelector(
    (state) => state.appointment?.response?.data
  );

  const propertySelector = useSelector((state) => state.propertyData);

  const addAppointmentSelector = useSelector((state) => state.appointment);

  // const handleInputArea = (e) => {
  //   const nextFormState = {
  //     ...form,
  //     pickup_location: e,
  //     pickup_latitude: "",
  //     pickup_longitude: "",
  //   };
  //   setForm(nextFormState);
  // };
  // const onSelect = (latLng, pickup_location) => {
  //   const nextFormState = {
  //     ...form,
  //     pickup_location: pickup_location,
  //     pickup_latitude: latLng.lat,
  //     pickup_longitude: latLng.lng,
  //   };
  //   setForm(nextFormState);
  // };

  const { response: res } = useSelector((state) => state.login);
  const role_id = res?.data?.role_id;
  const SMteam =
    SourcingTL === role_id ||
    SourcingManager === role_id ||
    sourcing_head_id === role_id;

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
  };

  useEffect(() => {
    dispatch(getVisiterList({ lead_id: leadId }));
  }, []);

  useEffect(() => {
    if (propertySelector.response) {
      const { response } = propertySelector;
      if (response?.status === 200) {
        setApiResponse(
          handleListResponse(response.data.filter(item => item.status === true))
        );        
      } else {
        setApiResponse([]);
      }
    }
  }, [propertySelector]);

  useEffect(() => {
    dispatch(getAgentProperty({ property_id: propertyId }));
  }, []);

  // JUSTO-88: After successful appointment create – redirect to previous screen and reset state so same details aren't submitted again.
  // Previous logic (commented out – redirect could be lost when create was overwritten by GET_VISITOR_LIST):
  // useEffect(() => {
  //   if (addAppointmentSelector.create === true) {
  //     if (addAppointmentSelector?.addResponse?.status === 200) {
  //       setForm({});
  //       if(SMteam){
  //         navigate("/appointment_site_visit")
  //       }else{
  //         navigatpage("/appointment");
  //       }
  //     }
  //   }
  // }, [addAppointmentSelector]);
  useEffect(() => {
    const status = addAppointmentSelector?.addResponse?.status;
    const isSuccess = status === 200 || status === 201;
    if (!isSuccess) return;

    setForm({});
    if (SMteam) {
      navigate("/appointment_site_visit");
    } else {
      navigate("/appointment");
    }
    dispatch(resetAddAppointment()); // JUSTO-88: Clear add-appointment state so duplicate submit with same data is prevented.
  }, [addAppointmentSelector?.addResponse, dispatch, SMteam, navigate]);

  const handleSubmit = () => {
    if (!handleValid()) {
      const payload = {
        lead_id: form.lead_id,
        property_id: form.property_id,
        appointment_date: form.appointment_date,
        appointment_time: form.appointment_time,
        visit_status: strings.warm,
        type: 1,
      };
      if (form.remarks) {
        payload.remark = form.remarks;
      }
      dispatch(addAppointment(payload));
    }
  };
  const handleValid = () => {
    let err = {};
    let formError = false;
    if (!form.lead_id) {
      formError = true;
      err["lead_id"] = strings.leadErr;
    }
    if (!form.property_id) {
      formError = true;
      err["property_id"] = strings.propertynameErr;
    }
    if (!form.appointment_date) {
      formError = true;
      err["appointment_date"] = strings.followup_dateErr;
    }
    if (!form.appointment_time) {
      formError = true;
      err["appointment_time"] = strings.followup_timeErr;
    }
    // if (form.pickup === "Yes") {
    //   if (!form.pickup_location) {
    //     formError = true;
    //     err["pickup_location"] = strings.pickupLocationErr;
    //   }
    //   if (!form.pickup_address) {
    //     formError = true;
    //     err["pickup_address"] = strings.addressErr;
    //   }
    //   if (!form.number_of_guest) {
    //     formError = true;
    //     err["number_of_guest"] = strings.numberOfGuest;
    //   } else if (isNaN(form.number_of_guest)) {
    //     formError = true;
    //     err["number_of_guest"] = strings.validNumber;
    //   }
    // }
    setFormVaild(err);
    return formError;
  };
  useEffect(() => {
    seo({
      title: "Add Appointment",
      metaDescription: "Add appointment details",
      canonicalLink: window.location.href,
    });
  }, []);

  const selectedDate = form.appointment_date
    ? moment(form.appointment_date, "YYYY-MM-DD").toDate()
    : null;

  const leadOptions = useMemo(
    () =>
      (getVisitorListSelector || []).map((item) => ({
        value: item._id,
        label: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
        property_id: item.property_id,
      })),
    [getVisitorListSelector],
  );

  const propertyOptions = useMemo(
    () =>
      (apiResponse || []).map((item) => ({
        value: item.property_id,
        label: item.property_title,
      })),
    [apiResponse],
  );

  const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  };

  return (
    <div className="page-body justo-appointment-page">
      <Breadcrumb
        title={strings.addAppointment}
        url={"/appointment"}
        parentListing={strings.appointmentList}
        titleactive={strings.addAppointment}
        isBackBtn={true}
      />
      <div className="container-fluid justo-appointment">
        <div className="justo-appointment__card justo-appointment-form">
          <div className="justo-appointment-form__top">
            <div className="justo-appointment__title-block">
              <h1 className="justo-appointment__title">
                {strings.addAppointment}
              </h1>
              <p className="justo-appointment__subtitle">
                {strings.addAppointmentSubtitle}
              </p>
            </div>
            <div className="justo-appointment-form__top-actions">
              <button
                className="justo-appointment__btn justo-appointment__btn--ghost"
                type="button"
                onClick={() => navigate(SMteam ? "/appointment_site_visit" : "/appointment")}
              >
                {strings.cancel}
              </button>
              <button
                className="justo-appointment__btn justo-appointment__btn--solid"
                type="button"
                onClick={handleSubmit}
              >
                <i className="fa fa-check" aria-hidden="true" />
                {strings.submit}
              </button>
            </div>
          </div>
          <div className="justo-appointment-form__body">
            <div className="justo-appointment-form__panel">
              <div className="justo-appointment__field">
                <label className="justo-appointment__label" htmlFor="addAptLead">
                  {strings.selectLead}
                  <span className="required-field" />
                </label>
                <Select
                  inputId="addAptLead"
                  classNamePrefix="justo-appointment-select"
                  isSearchable
                  isClearable={!leadId}
                  isDisabled={Boolean(leadId)}
                  placeholder={strings.selectlead}
                  options={leadOptions}
                  value={
                    leadOptions.find((option) => option.value === form.lead_id) ||
                    null
                  }
                  onChange={(option) => {
                    setForm({
                      ...form,
                      lead_id: option?.value || "",
                      property_id: option?.property_id || "",
                    });
                  }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
                {formVaild?.lead_id && (
                  <span className="justo-appointment__error">
                    {formVaild.lead_id}
                  </span>
                )}
              </div>
              <div className="justo-appointment__field">
                <label className="justo-appointment__label" htmlFor="addAptProperty">
                  {strings.selectProperty}
                  <span className="required-field" />
                </label>
                <Select
                  inputId="addAptProperty"
                  classNamePrefix="justo-appointment-select"
                  isSearchable
                  isClearable={!leadId}
                  isDisabled={Boolean(leadId)}
                  placeholder={strings.selectProp}
                  options={propertyOptions}
                  value={
                    propertyOptions.find(
                      (option) => option.value === form.property_id,
                    ) || null
                  }
                  onChange={(option) => {
                    setForm({
                      ...form,
                      property_id: option?.value || "",
                      property_title: option?.label || "",
                    });
                  }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
                {formVaild?.property_id && (
                  <span className="justo-appointment__error">
                    {formVaild.property_id}
                  </span>
                )}
              </div>
              <AppointmentScheduleFields
                dateId="addAptDate"
                hourId="addAptHour"
                selectedDate={selectedDate}
                onDateChange={(date) => {
                  setForm((prev) => ({
                    ...prev,
                    appointment_date: date
                      ? moment(date).format("YYYY-MM-DD")
                      : "",
                  }));
                }}
                appointmentTime={form.appointment_time || ""}
                onTimeChange={(appointment_time) => {
                  setForm((prev) => ({ ...prev, appointment_time }));
                  if (appointment_time && formVaild?.appointment_time) {
                    const nextErrors = { ...formVaild };
                    delete nextErrors.appointment_time;
                    setFormVaild(isEmpty(nextErrors) ? null : nextErrors);
                  }
                }}
                dateError={formVaild?.appointment_date}
                timeError={formVaild?.appointment_time}
              />
              <div className="justo-appointment__field justo-appointment-form__field--full">
                <label className="justo-appointment__label" htmlFor="addAptRemarks">
                  {strings.comment}
                </label>
                <textarea
                  id="addAptRemarks"
                  className="justo-appointment__input"
                  name="remarks"
                  rows="4"
                  value={form?.remarks || ""}
                  onChange={handleInputField}
                />
              </div>
            </div>
            {!isEmpty(formVaild) && (
              <p className="justo-appointment-form__error-banner">
                {strings.fieldRequired}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AddAppointment;
